/**
 * Apollo GraphQL Client Configuration
 */

import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';

const httpLink = createHttpLink({
  uri: `${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/graphql`,
});

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      console.error(`GraphQL error: Message: ${message}, Location: ${locations}, Path: ${path}`);
      
      // Log detailed error information
      if (extensions) {
        console.error('GraphQL error details:', extensions);
      }
    });
  }
  
  if (networkError) {
    console.error(`Network error: ${networkError}`);
    
    // Check if it's a CORS or connection error
    if ('statusCode' in networkError) {
      console.error(`Network error status: ${networkError.statusCode}`);
    }
  }
});

// Auth link (if needed for future authentication)
const authLink = setContext((_, { headers }) => {
  // Get the authentication token from local storage if it exists
  const token = localStorage.getItem('token');
  
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    }
  };
});

// Create Apollo Client
export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          offers: {
            // Configure caching for paginated offers
            keyArgs: ['filters'],
            merge(existing = [], incoming) {
              return [...existing, ...incoming];
            },
          },
          products: {
            // Configure caching for paginated products
            keyArgs: ['filters'],
            merge(existing = [], incoming) {
              return [...existing, ...incoming];
            },
          },
        },
      },
      Offer: {
        fields: {
          products: {
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          cities: {
            merge(existing = [], incoming) {
              return incoming;
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    },
    query: {
      errorPolicy: 'all',
    },
  },
});

export default apolloClient;